import _ from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import BenefitsBucksImageEn from '../../../../../common/assets/images/menu/meniu-banner-benefit-bucks_en.webp';
import BenefitsBucksImageRo from '../../../../../common/assets/images/menu/meniu-banner-benefit-bucks_ro.webp';
import FlexibleBenefitsImageEn from '../../../../../common/assets/images/menu/meniu-banner-flexible-benefits_en.webp';
import FlexibleBenefitsImageRo from '../../../../../common/assets/images/menu/meniu-banner-flexible-benefits_ro.webp';
import { config } from '../../../config';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { RedirectCategoryTypes } from '../../../enums';
import { ICategoryMenuItem } from '../../../Pages/BenefitsPage/types';

interface IBenefitsNavProps {
  noColumns: number;
  benefitsType: string;
  categories: ICategoryMenuItem[] | undefined;
  showTaxes: boolean;
  onClick?: () => void;
}

const CATEGORY_POINTS = {
  categoryTitle: 2,
  categoryChild: 1,
};

export default function BenefitsNav({
  noColumns,
  categories,
  benefitsType,
  //showTaxes,
  onClick,
}: IBenefitsNavProps) {
  /*  const { t: translate } = useTranslation();*/
  const { customerInfo } = useContext(CustomerInfoContext);

  const constructCategoryColumns = useCallback(
    (
      categories: ICategoryMenuItem[],
      rowsPerCategory: number[],
      columns: ICategoryMenuItem[][],
      columnFill: number,
      currentColumn: number,
      categoryIndex: number,
      maxRowsPerColumn: number,
    ) => {
      while (categoryIndex < categories.length) {
        const relativeColumn =
          categoryIndex && columnFill ? currentColumn + 1 : currentColumn;
        const incrementedColumn =
          currentColumn + 1 >= noColumns ? currentColumn : currentColumn + 1;

        if (categories.length - categoryIndex <= noColumns - relativeColumn) {
          columns[relativeColumn].push(categories[categoryIndex]);
          currentColumn = incrementedColumn;
        } else {
          if (columnFill + rowsPerCategory[categoryIndex] <= maxRowsPerColumn) {
            columnFill += rowsPerCategory[categoryIndex];
          } else {
            currentColumn = incrementedColumn;
            columnFill = rowsPerCategory[categoryIndex];
          }

          columns[currentColumn].push(categories[categoryIndex]);
        }

        categoryIndex += 1;
      }

      return columns;
    },
    [noColumns],
  );

  const getMaxRowsPerColumn = useMemo(
    () =>
      (categoriesRows: number[]): number => {
        let currentColumnRows: number = 0;
        let remainingColumns = noColumns;

        const categoriesMaxRows = Math.max(...categoriesRows);
        const averageRows =
          categoriesRows.reduce((acc, el) => acc + el, 0) / noColumns;

        for (const categoryRows of categoriesRows) {
          currentColumnRows += categoryRows;

          if (currentColumnRows > averageRows && remainingColumns > 1) {
            currentColumnRows = categoryRows;
            remainingColumns--;
          }
        }

        const maxRows =
          averageRows +
          Math.ceil(Math.abs(currentColumnRows - averageRows) / noColumns);

        return Math.max(categoriesMaxRows, maxRows);
      },
    [noColumns],
  );

  const formattedCategoriesPerColumn = useMemo(() => {
    if (!categories) {
      return [];
    }

    const visibleCategories = categories
      .filter(category => !category.isSaveEveryday)
      .map(category => ({
        ...category,
        children: category.children?.filter(
          childCategory => !childCategory.isSaveEveryday,
        ),
      }));

    const rowSpacesPerCategory = visibleCategories.map(
      category =>
        CATEGORY_POINTS.categoryTitle +
        ((category.showSubmenu ? category?.children?.length : 0) ?? 0) *
          CATEGORY_POINTS.categoryChild,
    );

    const maxRowsPerColumn = getMaxRowsPerColumn(rowSpacesPerCategory);

    let columns = Array.from(
      { length: noColumns },
      () => [] as ICategoryMenuItem[],
    );

    let columnFill = 0;
    let currentColumn = 0;
    let categoryIndex = 0;

    return constructCategoryColumns(
      visibleCategories,
      rowSpacesPerCategory,
      columns,
      columnFill,
      currentColumn,
      categoryIndex,
      maxRowsPerColumn,
    );
  }, [categories, noColumns, constructCategoryColumns, getMaxRowsPerColumn]);

  const language = customerInfo?.languageCode ?? config.defaultLanguage;

  return (
    <div className="category-nav-panel category-nav-marketing">
      <div className={`category-nav-list nav-columns-${noColumns}`}>
        {formattedCategoriesPerColumn.map(
          (categoriesColumn: ICategoryMenuItem[], index: number) => (
            <div key={index}>
              {categoriesColumn.map((categoryColumn: ICategoryMenuItem) => (
                <div
                  className="category-lvl"
                  key={categoryColumn.id}
                  onClick={onClick}
                >
                  <Link to={getRedirectLink(benefitsType, categoryColumn)}>
                    {_.unescape(categoryColumn.title)}
                  </Link>

                  {categoryColumn.showSubmenu && (
                    <ul>
                      {categoryColumn.children?.map(childCategory => (
                        <li key={childCategory.id}>
                          <Link
                            to={getRedirectLink(benefitsType, childCategory)}
                          >
                            {_.unescape(childCategory.title)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ),
        )}
      </div>

      <div className="category-nav-mkt-box">
        {benefitsType === 'flexible-benefits' ? (
          <>
            <Link to="/flexible-benefits">
              <img
                src={
                  language === 'ro'
                    ? FlexibleBenefitsImageRo
                    : FlexibleBenefitsImageEn
                }
                alt="Flexible benefits"
              />
            </Link>
          </>
        ) : (
          <>
            <Link to="/club-benefits/7054">
              <img
                src={
                  language === 'ro'
                    ? BenefitsBucksImageRo
                    : BenefitsBucksImageEn
                }
                alt="Flexible benefits"
              />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

const getRedirectLink = (
  benefitsType: string,
  item: ICategoryMenuItem,
): string => {
  if (!item.redirectCategoryId) {
    return `/${benefitsType}/${item.id}`;
  }

  if (item.redirectType === RedirectCategoryTypes.Flexible) {
    return `/flexible-benefits/${item.redirectCategoryId}`;
  }

  if (item.redirectType === RedirectCategoryTypes.Club) {
    return `/club-benefits/${item.redirectCategoryId}`;
  }

  return '';
};
